import { Chart, Traceable } from './assistant'
import { ResponseData, SuccessResponseDTO, TimeType } from './generic'
import { Language } from './organization'
import { StructuredDataStatus, StructuredDataValueDTO } from './structured-data'

export type BeneficiaryStatus = 'enabled' | 'disabled' | 'deleted'

export type SortDirection = 'ASC' | 'DESC'

export type SortOption = {
  field: string
  direction: SortDirection
}

export type BeneficiaryDTO = {
  caseWorkerIds: string[]
  createdAt: TimeType
  fullName: string
  id: string
  language?: Language
  profile: { value: string } & Traceable
  status: BeneficiaryStatus
  updatedAt: TimeType
  structuredData: { value: StructuredDataValueDTO } & Traceable
}
export type SuggestedTipDTO = {
  title: string
  content: string
}
export type OrganizationStructuredDataStateDTO = {
  currentStatusEpoch: number
  currentStatus: StructuredDataStatus
  totalBeneficiaries: number
  totalUpdatedBeneficiaries: number
}

export type BeneficiaryMetaDataDTO = {
  id: string
  currentPeriod: {
    lastNoteCreatedAt: number | null
    taskCount: number
  }
  previousPeriod: {
    lastNoteCreatedAt: number | null
    taskCount: number
  }
}

export type DeleteBeneficiaryResponse = ResponseData<SuccessResponseDTO>
export type OutdateAllStructuredDataBeneficiaryResponse = ResponseData<SuccessResponseDTO>
export type GetAllStructuredDataCurrentStatusResponse = ResponseData<OrganizationStructuredDataStateDTO>

// ================================ FILTER ================================

export type BeneficiaryFilterFullTextType = {
  key: 'content'
  type: 'fullText'
  value: string
}

export type BeneficiaryFilterBaseType = {
  key: string
  type: 'string' | 'number' | 'boolean' | 'enum' | 'date' | 'multiselect'
}

export type BeneficiaryFilterStringType = BeneficiaryFilterBaseType & {
  type: 'string'
} & ({ operator: 'contains'; value: string } | { operator: 'is_null' | 'is_not_null' })

export type BeneficiaryFilterNumberType = BeneficiaryFilterBaseType & {
  type: 'number'
} & (
    | { operator: 'eq' | 'neq' | 'gt' | 'lt'; value: number }
    | { operator: 'between'; value: number; otherValue: number }
    | { operator: 'is_null' | 'is_not_null' }
  )

export type BeneficiaryFilterBooleanType = BeneficiaryFilterBaseType & {
  type: 'boolean'
  operator: 'eq'
  value: Boolean_BooleanNull
}

export type BeneficiaryFilterEnumType = BeneficiaryFilterBaseType & {
  type: 'enum'
  operator: 'in'
  value: (string | null)[]
}

export type BeneficiaryFilterMultiselectType = BeneficiaryFilterBaseType & {
  type: 'multiselect'
  operator: 'in'
  value: (string | null)[]
}

export type BeneficiaryFilterDateType = (BeneficiaryFilterBaseType & {
  type: 'date'
}) &
  (
    | {
        // Absolute date filters
        operator: 'eq' | 'neq' | 'gt' | 'lt'
        value: number
      }
    | {
        // Relative date filters
        operator: 'in_the_last' | 'in_the_next'
        days: number
      }
    | {
        // Relative date filters
        operator: 'current_week' | 'current_month' | 'current_year'
      }
    | {
        // Absolute date filters
        operator: 'between'
        value: number
        otherValue: number
      }
    | { operator: 'is_null' | 'is_not_null' }
  )

export type BeneficiaryFilterAllTypes =
  | BeneficiaryFilterBooleanType
  | BeneficiaryFilterDateType
  | BeneficiaryFilterEnumType
  | BeneficiaryFilterMultiselectType
  | BeneficiaryFilterNumberType
  | BeneficiaryFilterStringType

export type BeneficiaryFilterItem = BeneficiaryFilterFullTextType | BeneficiaryFilterAllTypes

export type BeneficiaryFilters = BeneficiaryFilterItem[]

export type BeneficiaryFiltersDTO = BeneficiaryFilters

// ================================ LLM FILTER ================================

export type BeneficiaryFilterLlmBaseType = {
  label: string
  type: 'string' | 'number' | 'boolean' | 'enum' | 'date'
}

export type BeneficiaryFilterLlmStringType = BeneficiaryFilterLlmBaseType & {
  type: 'string'
  operator: 'contains'
  value: string
}

export type BeneficiaryFilterLlmNumberType = BeneficiaryFilterLlmBaseType & {
  type: 'number'
  operator: 'eq' | 'neq' | 'gt' | 'lt'
  value: number
  otherValue?: number
}

export type BeneficiaryFilterLlmBooleanType = BeneficiaryFilterLlmBaseType & {
  type: 'boolean'
  operator: 'eq'
  value: Boolean_BooleanNull
}

export type BeneficiaryFilterLlmEnumType = BeneficiaryFilterLlmBaseType & {
  type: 'enum'
  operator: 'in'
  value: (string | null)[]
}

export type BeneficiaryFilterLlmDateType = BeneficiaryFilterLlmBaseType &
  (
    | {
        type: 'date'
        // Absolute date filters
        operator: 'eq' | 'neq' | 'gt' | 'lt'
        value: string
      }
    | {
        type: 'date'
        // Relative date filters
        operator: 'in_the_last' | 'in_the_next'
        days: number
      }
    | {
        type: 'date'
        // Relative date filters
        operator: 'current_week' | 'current_month' | 'current_year'
      }
    | {
        type: 'date'
        // Absolute date filters
        operator: 'between'
        value: string
        otherValue: string
      }
    | { type: 'date'; operator: 'is_null' | 'is_not_null' }
  )

export type BeneficiaryFilterLlmAllTypes =
  | BeneficiaryFilterLlmStringType
  | BeneficiaryFilterLlmNumberType
  | BeneficiaryFilterLlmBooleanType
  | BeneficiaryFilterLlmEnumType
  | BeneficiaryFilterLlmDateType

export type BeneficiaryFilterLlmItem = BeneficiaryFilterLlmAllTypes

export type BeneficiaryFilterLlmItems = BeneficiaryFilterLlmItem[]

// Filter templates

export type FilterTemplate = {
  createdAt: TimeType
  filters: BeneficiaryFilters
  id: string
  name: string
  updatedAt: TimeType
}

export type FilterTemplateDTO = FilterTemplate

export const NULL_AS_STRING = 'unknown' as const

export type Boolean_BooleanNull = boolean | null
export type Enum_StringNull = string | null
export type Multiselect_StringArrayNull = string[] | null
export type Number_NumberNull = number | null
export type Date_NumberNull = TimeType | null
export type String_StringNull = string | null

// ================================ AGGREGATE CONTRACT ================================

/**
 * Example payload:
 * {
 *   filters: [
 *     { key: 'field-gender', type: 'enum', operator: 'in', value: ['Female'] },
 *     { key: 'field-children', type: 'number', operator: 'eq', value: 2 },
 *   ],
 *   dimensions: [
 *     {
 *       key: 'field-age',
 *       buckets: [
 *           { label: '0-20', min: 0, max: 20 },
 *           { label: '20-40', min: 20, max: 40 },
 *           { label: '40+', min: 40 },
 *         ],
 *       },
 *   ],
 *   metrics: [
 *     { key: '*', operation: 'count' },
 *     { key: 'field-income', operation: 'average' },
 *   ],
 * }
 */
export const beneficiaryAggregateOperations = ['count', 'sum', 'average', 'min', 'max'] as const

export type BeneficiaryAggregateOperation = (typeof beneficiaryAggregateOperations)[number]

export type BeneficiaryAggregateBooleanBucket = {
  label: string
  value: boolean
  color?: string
}

export type BeneficiaryAggregateEnumBucket = {
  label: string
  values: string[]
  color?: string
}

export type BeneficiaryAggregateMultiselectBucket = {
  label: string
  values: string[]
  color?: string
}

export type BeneficiaryAggregateNumberBucket = {
  label: string
  min?: number
  max?: number
  color?: string
}

export type BeneficiaryAggregateDateBucket = {
  label: string
  min?: number
  max?: number
  color?: string
}

type BeneficiaryAggregateBaseDimension<T extends { label: string }> = {
  key: string
  buckets: T[]
}

export type BeneficiaryTimeSeriesDimension = {
  key: string
  timeSeries: {
    groupBy: 'day' | 'week' | 'month' | 'year'
    range: {
      unit: 'day' | 'week' | 'month' | 'year'
      value: number
    }
  }
  buckets: { label: string }[] // virtual buckets, filled by the backend (and not by the LLM)
}

export type BeneficiaryAggregateBooleanDimension = BeneficiaryAggregateBaseDimension<BeneficiaryAggregateBooleanBucket>
export type BeneficiaryAggregateMultiselectDimension =
  BeneficiaryAggregateBaseDimension<BeneficiaryAggregateMultiselectBucket>
export type BeneficiaryAggregateEnumDimension = BeneficiaryAggregateBaseDimension<BeneficiaryAggregateEnumBucket>
export type BeneficiaryAggregateNumberDimension = BeneficiaryAggregateBaseDimension<BeneficiaryAggregateNumberBucket>
export type BeneficiaryAggregateDateDimension = BeneficiaryAggregateBaseDimension<BeneficiaryAggregateDateBucket>

export type BeneficiaryAggregateDimension =
  | BeneficiaryAggregateBooleanDimension
  | BeneficiaryAggregateMultiselectDimension
  | BeneficiaryAggregateEnumDimension
  | BeneficiaryAggregateNumberDimension
  | BeneficiaryAggregateDateDimension

export type BeneficiaryInsightDimension = BeneficiaryTimeSeriesDimension | BeneficiaryAggregateDimension

export type BeneficiaryInsightDimensions =
  | [BeneficiaryInsightDimension]
  | [BeneficiaryInsightDimension, BeneficiaryInsightDimension]

export type BeneficiaryDimensionsDTO = BeneficiaryInsightDimensions

// LLM dimensions are mostly the same as the NON-LLM dimensions except for the date type dimension.
export type BeneficiaryLlmAggregateMultiselectBucket = BeneficiaryAggregateMultiselectBucket
export type BeneficiaryLlmAggregateEnumBucket = BeneficiaryAggregateEnumBucket
export type BeneficiaryLlmAggregateNumberBucket = BeneficiaryAggregateNumberBucket
export type BeneficiaryLlmAggregateDateBucket = {
  label: string
  min?: string
  max?: string
}

export type BeneficiaryLlmAggregateBooleanDimension = BeneficiaryAggregateBooleanDimension
export type BeneficiaryLlmAggregateMultiselectDimension = BeneficiaryAggregateMultiselectDimension
export type BeneficiaryLlmAggregateEnumDimension = BeneficiaryAggregateEnumDimension
export type BeneficiaryLlmAggregateNumberDimension = BeneficiaryAggregateNumberDimension
export type BeneficiaryLlmAggregateDateDimension =
  BeneficiaryAggregateBaseDimension<BeneficiaryLlmAggregateDateBucket> & {
    timeSeries?: {
      groupBy: 'day' | 'week' | 'month' | 'year'
      range: {
        unit: 'day' | 'week' | 'month' | 'year'
        value: number
      }
    }
  }

export type BeneficiaryLlmInsightDimension =
  | BeneficiaryLlmAggregateEnumDimension
  | BeneficiaryLlmAggregateNumberDimension
  | BeneficiaryLlmAggregateDateDimension

export type BeneficiaryLlmInsightDimensions =
  | [BeneficiaryLlmInsightDimension]
  | [BeneficiaryLlmInsightDimension, BeneficiaryLlmInsightDimension]

export type BeneficiaryInsightDimensionsDTO = BeneficiaryInsightDimensions

// Metrics are the same for both NON-LLM and LLM
export type BeneficiaryInsightMetric = {
  key: string
  operation: BeneficiaryAggregateOperation
}
export type BeneficiaryInsightMetricDTO = {
  key: string
  operation: BeneficiaryAggregateOperation
}

export type BeneficiaryInsightMetrics = BeneficiaryInsightMetric[]
export type BeneficiaryInsightMetricsDTO = BeneficiaryInsightMetricDTO[]

export type BeneficiaryLlmMetrics = BeneficiaryInsightMetrics

export // ================================ INSIGHTS RESULTS ================================

type BeneficiaryInsightRawRows = (
  | {
      metrics: BeneficiaryInsightMetricResult
    }
  | ({
      metrics: BeneficiaryInsightMetricResult
    } & { [key in Exclude<string, 'metrics'>]: string })
)[]

export type BeneficiaryInsightMetricResult = {
  key: string
  operation: BeneficiaryAggregateOperation
  value: number
}[]

export type BeneficiaryInsightRow = {
  metrics: BeneficiaryInsightMetricResult
  dimensions?: {
    key: string
    value: string
  }[]
}

export type BeneficiaryInsightRows = BeneficiaryInsightRow[]

export const BENEFICIARY_INSIGHT_CHART_TYPES = ['pie', 'bar', 'line'] as const

export const BENEFICIARY_INSIGHT_DISPLAY_TYPES = ['kpi', ...BENEFICIARY_INSIGHT_CHART_TYPES] as const

export type BeneficiaryInsightChartType = (typeof BENEFICIARY_INSIGHT_CHART_TYPES)[number]
export type BeneficiaryInsightDisplayType = (typeof BENEFICIARY_INSIGHT_DISPLAY_TYPES)[number]

export type BeneficiaryInsightDisplay = {
  type: BeneficiaryInsightDisplayType | null
  title: string | null
  description: string | null
}

export type BeneficiaryInsightsDTO = {
  recommendedChart?: BeneficiaryInsightChartType
  rows: BeneficiaryInsightRows
}

// ================================ INSIGHT WIDGETS ================================

export type BeneficiaryInsightWidgetDTO = {
  id: string
  title: string
  description: string
  type: BeneficiaryInsightDisplayType
  width: number
  height: number
  filters: BeneficiaryFilters
  dimensions: BeneficiaryInsightDimensions
  metrics: BeneficiaryInsightMetrics
  position: number
  displayInDashboard: boolean
  caseWorker: {
    id: string
    firstName: string
    lastName: string
    email: string
  }
}

export type BeneficiaryComputedBaseInsightWidget = {
  id: string
  title: string
  description: string
  filters: BeneficiaryFilters
  width: number
  height: number
}

export type BeneficiaryComputedKpiInsightWidget = BeneficiaryComputedBaseInsightWidget & {
  type: 'kpi'
  value: string
}

export type BeneficiaryComputedChartInsightWidget = BeneficiaryComputedBaseInsightWidget & {
  type: 'chart'
  chart: Chart
  csv: string
}

export type BeneficiaryComputedInsightWidget =
  | BeneficiaryComputedKpiInsightWidget
  | BeneficiaryComputedChartInsightWidget

export type BeneficiaryComputedInsightWidgetDTO = BeneficiaryComputedInsightWidget

export type BeneficiaryCreateInsightWidgetDTO = {
  title: string
  description: string
  type: BeneficiaryInsightDisplayType
  width: number
  height: number
  filters: BeneficiaryFiltersDTO
  dimensions: BeneficiaryInsightDimensionsDTO
  metrics: BeneficiaryInsightMetricsDTO
}
